/** Composants graphiques (Recharts), thème-aware via la palette dataviz. */

import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import {
  type CalendarCell,
  conformityForDay,
  type DayConformity,
  MEAL_ORDER,
  MEAL_SHORT_LABELS,
  type TrainingPhase,
} from '@/domain';
import type { ChartColors } from '@/theme/palette';
import { MACRO_ROLE } from '@/theme/palette';

const H = 280;

// Couleur d'un apport selon sa conformité à l'objectif de la phase (vert = conforme).
// 'off' (récup hors bande) = orange neutre : ni bien ni mal, juste hors cible.
export function conformityColor(conformity: DayConformity, palette: ChartColors): string {
  if (conformity === 'good') return palette.good;
  if (conformity === 'off') return palette.warning;
  return palette.critical; // 'over' (sèche) ou 'under' (masse) = mauvaise direction
}

function shortDate(iso: string): string {
  try {
    return format(parseISO(iso), 'd MMM', { locale: fr });
  } catch {
    return iso;
  }
}

// Ticks Y compacts pour les gros totaux agrégés : 14200 → « 14k ».
function compactTick(value: number): string {
  return Math.abs(value) >= 10000 ? `${Math.round(value / 1000)}k` : String(value);
}

interface AxisProps {
  palette: ChartColors;
}

function commonAxes({ palette }: AxisProps) {
  return {
    grid: <CartesianGrid stroke={palette.grid} vertical={false} />,
    tickStyle: { fill: palette.textSecondary, fontSize: 11 },
  };
}

/** Courbe de poids : brut + MM7j + lignes cible/idéal + zone saine + paliers. */
export function WeightChart({
  data,
  targetKg,
  idealKg,
  palette,
  healthyRange,
  milestones = [],
}: {
  data: { date: string; weightKg: number; smoothed: number }[];
  targetKg: number;
  idealKg: number;
  palette: ChartColors;
  healthyRange?: { minKg: number; maxKg: number };
  milestones?: { targetWeightKg: number; label: string }[];
}) {
  const a = commonAxes({ palette });
  return (
    <ResponsiveContainer width="100%" height={H}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
        {a.grid}
        <XAxis dataKey="date" tickFormatter={shortDate} tick={a.tickStyle} minTickGap={28} />
        <YAxis domain={['dataMin - 1', 'dataMax + 1']} tick={a.tickStyle} width={40} />
        <Tooltip
          contentStyle={tooltipStyle(palette)}
          labelFormatter={(v) => shortDate(String(v))}
          formatter={(val: number, name) => [`${val.toFixed(1)} kg`, name]}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {/* Bandeau de poids « sain » (IMC 18.5–24.9). */}
        {healthyRange && (
          <ReferenceArea
            y1={healthyRange.minKg}
            y2={healthyRange.maxKg}
            fill={palette.good}
            fillOpacity={0.08}
            stroke={palette.good}
            strokeOpacity={0.25}
          />
        )}
        {milestones.map((m, i) => (
          <ReferenceLine
            key={i}
            y={m.targetWeightKg}
            stroke={palette.aqua}
            strokeDasharray="2 4"
            strokeOpacity={0.7}
          />
        ))}
        <ReferenceLine
          y={targetKg}
          stroke={palette.yellow}
          strokeDasharray="5 4"
          label={{ value: 'Cible', fill: palette.yellow, fontSize: 11, position: 'insideTopRight' }}
        />
        <ReferenceLine
          y={idealKg}
          stroke={palette.good}
          strokeDasharray="5 4"
          label={{
            value: 'Idéal',
            fill: palette.good,
            fontSize: 11,
            position: 'insideBottomRight',
          }}
        />
        <Line
          type="monotone"
          dataKey="weightKg"
          name="Poids"
          stroke={palette.blue}
          strokeWidth={1}
          dot={false}
          opacity={0.55}
        />
        <Line
          type="monotone"
          dataKey="smoothed"
          name="Tendance (MM7j)"
          stroke={palette.violet}
          strokeWidth={2.4}
          dot={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** Calories par jour : barres colorées selon le seuil + MM7j + ligne de seuil. */
export function CalorieChart({
  data,
  threshold,
  phase = 'cut',
  palette,
}: {
  data: { date: string; kcal: number; avg7: number }[];
  threshold: number;
  phase?: TrainingPhase;
  palette: ChartColors;
}) {
  const a = commonAxes({ palette });
  // Libellé du repère selon la phase (le seuil est un plancher-cible en masse).
  const refLabel =
    phase === 'bulk' ? 'Surplus visé' : phase === 'recovery' ? 'Maintenance' : 'Seuil';
  return (
    <ResponsiveContainer width="100%" height={H}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
        {a.grid}
        <XAxis dataKey="date" tickFormatter={shortDate} tick={a.tickStyle} minTickGap={28} />
        <YAxis tick={a.tickStyle} width={44} />
        <Tooltip
          contentStyle={tooltipStyle(palette)}
          labelFormatter={(v) => shortDate(String(v))}
          formatter={(val: number, name) => [`${Math.round(val)} kcal`, name]}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <ReferenceLine
          y={threshold}
          stroke={palette.textSecondary}
          strokeWidth={1.5}
          strokeDasharray="4 3"
          label={{
            value: refLabel,
            fill: palette.textSecondary,
            fontSize: 11,
            position: 'insideTopRight',
          }}
        />
        <Bar
          dataKey="kcal"
          name="Calories"
          fill={palette.textSecondary}
          radius={[3, 3, 0, 0]}
          maxBarSize={26}
        >
          {data.map((d, i) => (
            <Cell
              key={i}
              fill={conformityColor(conformityForDay(d.kcal, threshold, phase), palette)}
            />
          ))}
        </Bar>
        <Line
          type="monotone"
          dataKey="avg7"
          name="Moyenne 7j"
          stroke={palette.blue}
          strokeWidth={2.2}
          dot={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** Calories agrégées par semaine ou mois (total par période). */
export function BucketedCalorieChart({
  data,
  palette,
}: {
  data: { key: string; start: string; value: number }[];
  palette: ChartColors;
}) {
  const a = commonAxes({ palette });
  return (
    <ResponsiveContainer width="100%" height={H}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
        {a.grid}
        <XAxis dataKey="start" tickFormatter={shortDate} tick={a.tickStyle} minTickGap={28} />
        <YAxis tick={a.tickStyle} width={44} tickFormatter={compactTick} />
        <Tooltip
          contentStyle={tooltipStyle(palette)}
          labelFormatter={(_, payload) => payload?.[0]?.payload?.key ?? ''}
          formatter={(val: number) => [`${Math.round(val)} kcal`, 'Total']}
        />
        <Bar
          dataKey="value"
          name="Total"
          fill={palette.blue}
          radius={[3, 3, 0, 0]}
          maxBarSize={34}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** Hydratation par jour (barres ml) avec ligne d'objectif. */
export function WaterChart({
  data,
  targetMl,
  palette,
}: {
  data: { date: string; ml: number }[];
  targetMl: number;
  palette: ChartColors;
}) {
  const a = commonAxes({ palette });
  return (
    <ResponsiveContainer width="100%" height={H}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
        {a.grid}
        <XAxis dataKey="date" tickFormatter={shortDate} tick={a.tickStyle} minTickGap={28} />
        <YAxis tick={a.tickStyle} width={44} />
        <Tooltip
          contentStyle={tooltipStyle(palette)}
          labelFormatter={(v) => shortDate(String(v))}
          formatter={(val: number) => [`${Math.round(val)} ml`, 'Eau']}
        />
        <ReferenceLine
          y={targetMl}
          stroke={palette.blue}
          strokeDasharray="5 4"
          label={{
            value: 'Objectif',
            fill: palette.blue,
            fontSize: 11,
            position: 'insideTopRight',
          }}
        />
        <Bar dataKey="ml" name="Eau" radius={[3, 3, 0, 0]} maxBarSize={26}>
          {data.map((d, i) => (
            <Cell
              key={i}
              fill={d.ml >= targetMl ? palette.aqua : palette.blue}
              fillOpacity={d.ml >= targetMl ? 1 : 0.55}
            />
          ))}
        </Bar>
      </ComposedChart>
    </ResponsiveContainer>
  );
}

const MACRO_SERIES = [
  { key: 'protein', name: 'Protéines' },
  { key: 'carbs', name: 'Glucides' },
  { key: 'fat', name: 'Lipides' },
] as const;

/** Macros empilées (grammes) par bucket temporel. */
export function MacroStackChart({
  data,
  palette,
}: {
  data: Record<string, number | string>[];
  palette: ChartColors;
}) {
  const a = commonAxes({ palette });
  return (
    <ResponsiveContainer width="100%" height={H}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
        {a.grid}
        <XAxis dataKey="date" tickFormatter={shortDate} tick={a.tickStyle} minTickGap={28} />
        <YAxis tick={a.tickStyle} width={40} />
        <Tooltip
          contentStyle={tooltipStyle(palette)}
          labelFormatter={(v) => shortDate(String(v))}
          formatter={(val: number, name) => [`${Math.round(val)} g`, name]}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {MACRO_SERIES.map((s) => (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stackId="macros"
            stroke={palette[MACRO_ROLE[s.key]]}
            fill={palette[MACRO_ROLE[s.key]]}
            fillOpacity={0.75}
            strokeWidth={1.5}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** Répartition des calories par repas (barres empilées) par jour. */
const MEAL_COLOR: Record<(typeof MEAL_ORDER)[number], keyof ChartColors> = {
  breakfast: 'yellow',
  lunch: 'aqua',
  dinner: 'violet',
  snack: 'magenta',
};

export function MealBarChart({
  data,
  palette,
}: {
  data: Record<string, number | string>[];
  palette: ChartColors;
}) {
  const a = commonAxes({ palette });
  return (
    <ResponsiveContainer width="100%" height={H}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
        {a.grid}
        <XAxis dataKey="date" tickFormatter={shortDate} tick={a.tickStyle} minTickGap={28} />
        <YAxis tick={a.tickStyle} width={44} />
        <Tooltip
          contentStyle={tooltipStyle(palette)}
          labelFormatter={(v) => shortDate(String(v))}
          formatter={(val: number, name) => [`${Math.round(val)} kcal`, name]}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {MEAL_ORDER.map((m) => (
          <Bar
            key={m}
            dataKey={m}
            name={MEAL_SHORT_LABELS[m]}
            stackId="meals"
            fill={palette[MEAL_COLOR[m]]}
            maxBarSize={26}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** Nuage de points : déficit calorique moyen (x) vs variation de poids (y). */
export function CorrelationScatter({
  data,
  palette,
}: {
  data: { avgDailyDeficitKcal: number; weightDeltaKg: number }[];
  palette: ChartColors;
}) {
  const a = commonAxes({ palette });
  return (
    <ResponsiveContainer width="100%" height={H}>
      <ScatterChart margin={{ top: 8, right: 16, bottom: 16, left: 0 }}>
        {a.grid}
        <XAxis
          type="number"
          dataKey="avgDailyDeficitKcal"
          name="Écart"
          tick={a.tickStyle}
          unit=" kcal"
          label={{
            // Neutre + signe explicite : « bilan énergétique » est déjà employé
            // ailleurs (CaloriesPage) avec la convention inverse (positif = surplus).
            value: 'Écart au maintien moyen/j (> 0 = déficit)',
            position: 'insideBottom',
            offset: -8,
            fill: palette.textSecondary,
            fontSize: 11,
          }}
        />
        <YAxis
          type="number"
          dataKey="weightDeltaKg"
          name="Δ poids"
          tick={a.tickStyle}
          width={44}
          unit=" kg"
          label={{
            value: 'Δ poids / période (kg)',
            angle: -90,
            position: 'insideLeft',
            fill: palette.textSecondary,
            fontSize: 11,
            style: { textAnchor: 'middle' },
          }}
        />
        <ZAxis range={[60, 60]} />
        <ReferenceLine y={0} stroke={palette.textSecondary} strokeDasharray="3 3" />
        <ReferenceLine x={0} stroke={palette.textSecondary} strokeDasharray="3 3" />
        <Tooltip
          contentStyle={tooltipStyle(palette)}
          formatter={(val: number, name) => [
            name === 'Écart' ? `${Math.round(val)} kcal` : `${val} kg`,
            name,
          ]}
        />
        <Scatter data={data} fill={palette.blue} />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

/**
 * Perte de poids théorique (bilan énergétique, ~7700 kcal/kg) vs réelle (pesées lissées).
 * La courbe théorique n'est tracée QU'EN SÈCHE : hors sèche, projeter des kg via 7700 est
 * malhonnête (une prise n'est pas de la masse grasse pure ; en récup on vise l'équilibre).
 */
export function LossReconciliationChart({
  data,
  phase = 'cut',
  palette,
}: {
  data: { date: string; predictedLossKg: number; actualLossKg: number }[];
  phase?: TrainingPhase;
  palette: ChartColors;
}) {
  const a = commonAxes({ palette });
  const showPredicted = phase === 'cut';
  return (
    <ResponsiveContainer width="100%" height={H}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
        {a.grid}
        <XAxis dataKey="date" tickFormatter={shortDate} tick={a.tickStyle} minTickGap={28} />
        <YAxis tick={a.tickStyle} width={40} />
        <Tooltip
          contentStyle={tooltipStyle(palette)}
          labelFormatter={(v) => shortDate(String(v))}
          formatter={(val: number, name) => [`${val} kg`, name]}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <ReferenceLine y={0} stroke={palette.textSecondary} strokeDasharray="3 3" />
        {showPredicted && (
          <Line
            type="monotone"
            dataKey="predictedLossKg"
            name="Théorique"
            stroke={palette.blue}
            strokeWidth={2}
            dot={false}
            strokeDasharray="5 4"
          />
        )}
        <Line
          type="monotone"
          dataKey="actualLossKg"
          name="Réelle"
          stroke={palette.aqua}
          strokeWidth={2.4}
          dot={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** Donut de la répartition réelle des macros (grammes), cible affichée à part. */
export function MacroRings({
  actual,
  palette,
}: {
  actual: { protein: number; carbs: number; fat: number };
  palette: ChartColors;
}) {
  const data = [
    { name: 'Protéines', value: Math.round(actual.protein), color: palette[MACRO_ROLE.protein] },
    { name: 'Glucides', value: Math.round(actual.carbs), color: palette[MACRO_ROLE.carbs] },
    { name: 'Lipides', value: Math.round(actual.fat), color: palette[MACRO_ROLE.fat] },
  ];
  return (
    <ResponsiveContainer width="100%" height={240}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={55}
          outerRadius={85}
          paddingAngle={2}
          stroke={palette.surface}
          strokeWidth={2}
        >
          {data.map((d, i) => (
            <Cell key={i} fill={d.color} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle(palette)} formatter={(v: number, n) => [`${v} g`, n]} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

// Statut lisible d'une case selon la phase (pour le libellé accessible).
const CONFORMITY_TEXT: Record<TrainingPhase, Record<DayConformity, string>> = {
  cut: { good: 'sous le seuil', over: 'au-dessus du seuil', under: '', off: '' },
  bulk: { good: 'au-dessus du surplus', under: 'sous le surplus', over: '', off: '' },
  recovery: { good: 'dans la cible', off: 'hors cible', over: '', under: '' },
};

/** Heatmap calendrier : une case/jour, colorée selon la conformité à l'objectif de la
 * phase (vert = conforme), grise si non saisi. Colonnes = semaines (lundi→dimanche). */
export function CalendarHeatmap({
  matrix,
  threshold,
  phase = 'cut',
  palette,
}: {
  matrix: CalendarCell[][];
  threshold: number;
  phase?: TrainingPhase;
  palette: ChartColors;
}) {
  const cellColor = (value: number | null): string => {
    if (value == null || value === 0) return palette.grid;
    const conformity = conformityForDay(value, threshold, phase);
    const color = conformityColor(conformity, palette);
    // Intensité proportionnelle à l'écart au seuil (bornée), palier de base pour rester lisible.
    const ratio = Math.min(1, Math.abs(value - threshold) / threshold);
    const alpha = conformity === 'good' ? 40 + (1 - ratio) * 45 : 45 + ratio * 45;
    return `color-mix(in srgb, ${color} ${Math.round(alpha)}%, transparent)`;
  };
  // Libellé accessible : l'info ne repose pas sur la seule couleur (lecteurs d'écran).
  const cellLabel = (cell: CalendarCell): string => {
    if (!cell.date) return '';
    if (cell.value == null) return `${cell.date} : non saisi`;
    const kcal = Math.round(cell.value);
    const status =
      cell.value === 0
        ? 'non saisi'
        : CONFORMITY_TEXT[phase][conformityForDay(cell.value, threshold, phase)];
    return `${cell.date} : ${kcal} kcal, ${status}`;
  };
  // Échelle de légende : conforme (vert) → non conforme. En récup, l'écart est orange.
  const offColor = phase === 'recovery' ? palette.warning : palette.critical;
  const scale = [
    `color-mix(in srgb, ${palette.good} 45%, transparent)`,
    `color-mix(in srgb, ${palette.good} 85%, transparent)`,
    `color-mix(in srgb, ${offColor} 55%, transparent)`,
    `color-mix(in srgb, ${offColor} 90%, transparent)`,
  ];
  return (
    <>
      <div
        style={{ overflowX: 'auto' }}
        role="group"
        aria-label="Calendrier d'adhérence à l'objectif calorique"
      >
        <div style={{ display: 'flex', gap: 3 }}>
          {matrix.map((week, wi) => (
            <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {week.map((cell, di) => (
                <div
                  key={di}
                  role={cell.date ? 'img' : undefined}
                  aria-label={cell.date ? cellLabel(cell) : undefined}
                  title={cell.date ? cellLabel(cell) : ''}
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    background: cell.date ? cellColor(cell.value) : 'transparent',
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 8,
          fontSize: 11,
          color: palette.textSecondary,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          moins
          {scale.map((bg, i) => (
            <span key={i} style={{ width: 14, height: 14, borderRadius: 3, background: bg }} />
          ))}
          plus
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: palette.grid }} />
          non saisi
        </span>
      </div>
    </>
  );
}

/** Calories brûlées par jour (barres). */
export function ExerciseChart({
  data,
  palette,
}: {
  data: { date: string; kcal: number }[];
  palette: ChartColors;
}) {
  const a = commonAxes({ palette });
  return (
    <ResponsiveContainer width="100%" height={H}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
        {a.grid}
        <XAxis dataKey="date" tickFormatter={shortDate} tick={a.tickStyle} minTickGap={28} />
        <YAxis tick={a.tickStyle} width={44} />
        <Tooltip
          contentStyle={tooltipStyle(palette)}
          labelFormatter={(v) => shortDate(String(v))}
          formatter={(val: number) => [`${Math.round(val)} kcal`, 'Brûlé']}
        />
        <Bar
          dataKey="kcal"
          name="Brûlé"
          fill={palette.orange}
          radius={[3, 3, 0, 0]}
          maxBarSize={26}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** Déficit cumulé décomposé : alimentaire / sport / total (3 courbes). */
export function DeficitBreakdownChart({
  data,
  palette,
}: {
  data: {
    date: string;
    foodDeficitKcal: number;
    sportDeficitKcal: number;
    totalDeficitKcal: number;
  }[];
  palette: ChartColors;
}) {
  const a = commonAxes({ palette });
  return (
    <ResponsiveContainer width="100%" height={H}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
        {a.grid}
        <XAxis dataKey="date" tickFormatter={shortDate} tick={a.tickStyle} minTickGap={28} />
        <YAxis tick={a.tickStyle} width={44} tickFormatter={compactTick} />
        <Tooltip
          contentStyle={tooltipStyle(palette)}
          labelFormatter={(v) => shortDate(String(v))}
          formatter={(val: number, name) => [`${Math.round(val)} kcal`, name]}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <ReferenceLine
          y={0}
          stroke={palette.textSecondary}
          strokeDasharray="3 3"
          label={{
            value: 'Maintenance',
            position: 'insideTopLeft',
            fill: palette.textSecondary,
            fontSize: 10,
          }}
        />
        <Line
          type="monotone"
          dataKey="foodDeficitKcal"
          name="Alimentaire"
          stroke={palette.blue}
          strokeWidth={2}
          dot={false}
        />
        <Line
          type="monotone"
          dataKey="sportDeficitKcal"
          name="Sport"
          stroke={palette.orange}
          strokeWidth={2}
          dot={false}
        />
        <Line
          type="monotone"
          dataKey="totalDeficitKcal"
          name="Total"
          stroke={palette.aqua}
          strokeWidth={2.4}
          dot={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/** Mini-graphe de tendance sans axes ni légende (pour le récap). */
export function Sparkline({
  values,
  color,
  height = 44,
}: {
  values: number[];
  color: string;
  height?: number;
}) {
  const data = values.map((v, i) => ({ i, v }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 3, right: 3, bottom: 3, left: 3 }}>
        <Line
          type="monotone"
          dataKey="v"
          stroke={color}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function tooltipStyle(palette: ChartColors) {
  return {
    background: palette.surface,
    border: `1px solid ${palette.grid}`,
    borderRadius: 8,
    fontSize: 12,
    color: palette.textPrimary,
  };
}
